import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { FileInput } from "@astryxdesign/core/FileInput";
import { Table, proportional, pixel } from "@astryxdesign/core/Table";
import { companyService } from "../../services/companyService";
import * as XLSX from "xlsx";

export default function CSVImport({ onImportComplete, onClose }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Handle file selection (FileInput entrega o File diretamente)
  const handleFileSelect = async (file) => {
    if (!file) {
      handleReset();
      return;
    }

    const allowedExtensions = [".xlsx", ".xls"];
    if (
      !allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    ) {
      alert("Por favor, selecione um arquivo Excel (.xlsx, .xls) válido.");
      return;
    }

    setSelectedFile(file);
    setImportResult(null);
    setShowPreview(false);

    // Generate preview from Excel file
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Get header and first 5 rows
          const previewData = jsonData.slice(0, 6);
          setPreview(previewData);
        } catch (error) {
          console.error("Error reading Excel file:", error);
          setPreview([]);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error loading file:", error);
      setPreview([]);
    }
  };

  // Handle file upload and import
  const handleImport = async () => {
    if (!selectedFile) {
      alert("Por favor, selecione um arquivo Excel primeiro.");
      return;
    }

    setIsUploading(true);
    setImportResult(null);

    try {
      const result = await companyService.importFromFile(selectedFile);

      if (result.success) {
        setImportResult(result);
        if (onImportComplete) {
          onImportComplete(result);
        }
      } else {
        alert(`Erro na importação: ${result.message}`);
      }
    } catch (error) {
      alert("Erro ao importar arquivo Excel");
      console.error("Excel import error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedFile(null);
    setPreview([]);
    setImportResult(null);
    setShowPreview(false);
  };

  // Download sample CSV
  const downloadSample = () => {
    // Criar dados de exemplo apenas com campos essenciais
    const sampleData = [
      // Cabeçalho
      [
        "Código",
        "Nome da Empresa",
        "CNPJ",
        "Login do Portal",
        "Senha do Portal",
      ],
      // Dados de exemplo
      [
        "EMP001",
        "Empresa Exemplo LTDA",
        "12.345.678/0001-90",
        "usuario@empresa.com",
        "senha123",
      ],
      [
        "EMP002",
        "Outra Empresa ME",
        "98.765.432/0001-01",
        "login@outra.com",
        "minhasenha",
      ],
      [
        "EMP003",
        "Terceira Empresa SA",
        "11.222.333/0001-44",
        "acesso@terceira.com",
        "password",
      ],
    ];

    // Criar workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sampleData);

    // Definir larguras das colunas
    ws["!cols"] = [
      { width: 15 }, // Código
      { width: 25 }, // Nome da Empresa
      { width: 20 }, // CNPJ
      { width: 25 }, // Login do Portal
      { width: 15 }, // Senha do Portal
    ];

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, "Empresas");

    // Salvar arquivo
    XLSX.writeFile(wb, "exemplo_empresas.xlsx");
  };

  // Colunas do preview (derivadas do cabeçalho da planilha)
  const previewColumns = (preview[0] || []).map((header, idx) => ({
    key: String(idx),
    header: header || `Coluna ${idx + 1}`,
    width: proportional(1),
    renderCell: (r) => <Text size="sm">{r.cells[idx] ?? "-"}</Text>,
  }));
  const previewData = preview
    .slice(1, 6)
    .map((row, i) => ({ _id: i, cells: row }));

  // Colunas das tabelas de resultado (erros / sucessos)
  const resultColumns = (statusHeader, statusRender) => [
    {
      key: "row",
      header: "Linha",
      width: pixel(70),
      renderCell: (r) => <Text size="sm">{r.row}</Text>,
    },
    {
      key: "code",
      header: "Código",
      width: pixel(90),
      renderCell: (r) => <Text size="sm">{r.data.code || "-"}</Text>,
    },
    {
      key: "name",
      header: "Nome",
      width: proportional(2),
      renderCell: (r) => <Text size="sm">{r.data.name || "-"}</Text>,
    },
    {
      key: "cnpj",
      header: "CNPJ",
      width: proportional(1),
      renderCell: (r) => <Text size="sm">{r.data.cnpj || "-"}</Text>,
    },
    {
      key: "status",
      header: statusHeader,
      width: proportional(2),
      renderCell: statusRender,
    },
  ];

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={860}
    >
      <Stack gap={3}>
        <DialogHeader
          title="Importar Empresas via Excel"
          onOpenChange={(open) => {
            if (!open) onClose();
          }}
          hasDivider
        />

        {!importResult ? (
          <Stack gap={4}>
            {/* Instruções */}
            <Banner
              status="info"
              title="Instruções para importação"
              description={
                <Stack gap={1}>
                  <Text size="sm">
                    • O arquivo deve estar no formato Excel (.xlsx ou .xls) com
                    as colunas: Código | Nome da Empresa | CNPJ | Login do
                    Portal | Senha do Portal.
                  </Text>
                  <Text size="sm">
                    • <strong>Nome da Empresa</strong> e <strong>CNPJ</strong>{" "}
                    são obrigatórios; <strong>Código</strong> é opcional mas
                    recomendado.
                  </Text>
                  <Text size="sm">
                    • <strong>Login e Senha do Portal</strong> são opcionais.
                    Auto Download e Frequência usam os padrões (Ativo e Diário).
                  </Text>
                  <Text size="sm">
                    • CNPJs devem estar no formato: 12.345.678/0001-90.
                  </Text>
                </Stack>
              }
            >
              <Button
                label="Baixar Exemplo Excel"
                variant="secondary"
                size="sm"
                icon={<FontAwesomeIcon icon="download" />}
                onClick={downloadSample}
              />
            </Banner>

            {/* Seleção de arquivo */}
            <FileInput
              label="Selecionar arquivo Excel"
              mode="dropzone"
              accept=".xlsx,.xls"
              value={selectedFile}
              onChange={handleFileSelect}
            />
            {selectedFile && (
              <Text type="supporting">
                Arquivo selecionado: <strong>{selectedFile.name}</strong> (
                {(selectedFile.size / 1024).toFixed(1)} KB)
              </Text>
            )}

            {/* Preview */}
            {selectedFile && preview.length > 0 && (
              <Stack gap={2}>
                <HStack gap={2} vAlign="center" hAlign="between">
                  <Text weight="semibold">Preview do arquivo</Text>
                  <Button
                    label={showPreview ? "Ocultar Preview" : "Mostrar Preview"}
                    variant="ghost"
                    size="sm"
                    icon={
                      <FontAwesomeIcon
                        icon={showPreview ? "eye-slash" : "eye"}
                      />
                    }
                    onClick={() => setShowPreview(!showPreview)}
                  />
                </HStack>
                {showPreview && (
                  <Stack gap={1}>
                    <Table
                      data={previewData}
                      columns={previewColumns}
                      idKey="_id"
                      density="compact"
                    />
                    {preview.length > 6 && (
                      <Text type="supporting">
                        ... e mais {preview.length - 6} linha(s)
                      </Text>
                    )}
                  </Stack>
                )}
              </Stack>
            )}

            {/* Ações */}
            <HStack gap={2} hAlign="end">
              <Button label="Cancelar" variant="ghost" onClick={onClose} />
              <Button
                label={isUploading ? "Importando..." : "Importar Empresas"}
                variant="primary"
                icon={<FontAwesomeIcon icon="upload" />}
                isDisabled={!selectedFile}
                isLoading={isUploading}
                onClick={handleImport}
              />
            </HStack>
          </Stack>
        ) : (
          /* Resultados da importação */
          <Stack gap={4}>
            <Stack gap={2} hAlign="center">
              <Text color="accent">
                <FontAwesomeIcon
                  icon={
                    importResult.data.errors === 0
                      ? "check-circle"
                      : "exclamation-triangle"
                  }
                  size="2x"
                />
              </Text>
              <Heading level={3}>Importação Concluída</Heading>
              <Text type="supporting" justify="center">
                {importResult.message}
              </Text>
            </Stack>

            <Grid columns={{ minWidth: 120, max: 3 }} gap={3}>
              <Card padding={4}>
                <Stack gap={0.5} hAlign="center">
                  <Heading level={3} type="display-2">
                    {importResult.data.total}
                  </Heading>
                  <Text type="supporting">Total</Text>
                </Stack>
              </Card>
              <Card padding={4}>
                <Stack gap={0.5} hAlign="center">
                  <Heading level={3} type="display-2">
                    {importResult.data.imported}
                  </Heading>
                  <Text type="supporting">Importadas</Text>
                </Stack>
              </Card>
              <Card padding={4}>
                <Stack gap={0.5} hAlign="center">
                  <Heading level={3} type="display-2">
                    {importResult.data.errors}
                  </Heading>
                  <Text type="supporting">Erros</Text>
                </Stack>
              </Card>
            </Grid>

            {/* Erros */}
            {importResult.data.details.errors.length > 0 && (
              <Stack gap={2}>
                <Text weight="semibold">Erros encontrados</Text>
                <Stack
                  isScrollable
                  height={
                    importResult.data.details.errors.length > 6 ? 240 : undefined
                  }
                >
                  <Table
                    data={importResult.data.details.errors.map((e, i) => ({
                      ...e,
                      _id: i,
                    }))}
                    columns={resultColumns("Erro", (r) => (
                      <Text size="sm">{r.error}</Text>
                    ))}
                    idKey="_id"
                    density="compact"
                  />
                </Stack>
              </Stack>
            )}

            {/* Sucessos */}
            {importResult.data.details.success.length > 0 && (
              <Stack gap={2}>
                <Text weight="semibold">Empresas importadas com sucesso</Text>
                <Stack
                  isScrollable
                  height={
                    importResult.data.details.success.length > 6
                      ? 240
                      : undefined
                  }
                >
                  <Table
                    data={importResult.data.details.success.map((s, i) => ({
                      ...s,
                      _id: i,
                    }))}
                    columns={resultColumns("Status", () => (
                      <Text size="sm">Criada</Text>
                    ))}
                    idKey="_id"
                    density="compact"
                  />
                </Stack>
              </Stack>
            )}

            {/* Ações finais */}
            <HStack gap={2} hAlign="end">
              <Button
                label="Nova Importação"
                variant="ghost"
                icon={<FontAwesomeIcon icon="upload" />}
                onClick={handleReset}
              />
              <Button
                label="Concluir"
                variant="primary"
                icon={<FontAwesomeIcon icon="check" />}
                onClick={onClose}
              />
            </HStack>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
